import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {
  EVENT_COLORS,
  ICalendarEventRequest,
  ICalendarOccurrence,
  MONTHS,
  RECURRENCE_OPTIONS,
  recurrenceSummary,
  RecurrenceType,
  splitDateTime,
  WEEKDAYS,
} from './CalendarModel'

type Props = {
  show: boolean
  /** Düzenlenen seri; yoksa yeni kayıt */
  editing: ICalendarOccurrence | null
  /** Yeni kayıtta takvimde seçilen gün / aralık */
  initialStart?: Date | null
  initialEnd?: Date | null
  initialAllDay?: boolean
  saving: boolean
  onClose: () => void
  onSubmit: (request: ICalendarEventRequest) => void
}

interface IFormState {
  type: 'TASK' | 'EVENT'
  title: string
  description: string
  allDay: boolean
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  color: string
  shared: boolean
  recurrenceType: RecurrenceType
  interval: string
  weekdays: number[]
  monthDay: string
  month: string
  until: string
}

const pad = (value: number) => String(value).padStart(2, '0')
const dateOf = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
const timeOf = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`
/** ISO gün: 1 = Pazartesi ... 7 = Pazar */
const isoWeekday = (dateText: string) => {
  const day = new Date(`${dateText}T00:00:00`).getDay()
  return day === 0 ? 7 : day
}

function initialState(
  editing: ICalendarOccurrence | null,
  start?: Date | null,
  end?: Date | null,
  allDay?: boolean
): IFormState {
  if (editing) {
    const [startDate, startTime] = splitDateTime(editing.seriesStart)
    const [endDate, endTime] = splitDateTime(editing.seriesEnd)
    return {
      type: editing.type === 'TASK' ? 'TASK' : 'EVENT',
      title: editing.title,
      description: editing.description ?? '',
      allDay: editing.allDay,
      startDate,
      startTime,
      endDate,
      endTime,
      color: editing.color || EVENT_COLORS[0],
      shared: editing.shared,
      recurrenceType: editing.recurrenceType,
      interval: String(editing.recurrenceInterval ?? (editing.recurrenceType === 'EVERY_N_DAYS' ? 2 : 1)),
      weekdays: editing.recurrenceWeekdays ?? [isoWeekday(startDate)],
      monthDay: String(editing.recurrenceMonthDay ?? Number(startDate.substring(8, 10))),
      month: String(editing.recurrenceMonth ?? Number(startDate.substring(5, 7))),
      until: editing.recurrenceUntil ?? '',
    }
  }
  const now = new Date()
  const startAt = start ?? new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1)
  // Gün görünümünde seçilen aralık, ay görünümünde bir saatlik etkinlik
  const endAt = end && !allDay ? end : new Date(startAt.getTime() + 60 * 60 * 1000)
  const startDate = dateOf(startAt)
  return {
    type: 'EVENT',
    title: '',
    description: '',
    allDay: !!allDay,
    startDate,
    startTime: allDay ? '09:00' : timeOf(startAt),
    endDate: allDay ? startDate : dateOf(endAt),
    endTime: allDay ? '10:00' : timeOf(endAt),
    color: EVENT_COLORS[0],
    shared: false,
    recurrenceType: 'NONE',
    interval: '2',
    weekdays: [isoWeekday(startDate)],
    monthDay: String(Number(startDate.substring(8, 10))),
    month: String(Number(startDate.substring(5, 7))),
    until: '',
  }
}

/** Görev / etkinlik oluşturma ve seri düzenleme (tekrar kuralıyla) */
export const EventFormModal: FC<Props> = ({
  show,
  editing,
  initialStart,
  initialEnd,
  initialAllDay,
  saving,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<IFormState>(() => initialState(null))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    setForm(initialState(editing, initialStart, initialEnd, initialAllDay))
    setError(null)
  }, [show, editing, initialStart, initialEnd, initialAllDay])

  const set = <K extends keyof IFormState>(key: K, value: IFormState[K]) =>
    setForm((current) => ({...current, [key]: value}))

  const toggleWeekday = (day: number) =>
    set('weekdays', form.weekdays.includes(day) ? form.weekdays.filter((item) => item !== day) : [...form.weekdays, day])

  const buildRequest = (): ICalendarEventRequest | string => {
    if (!form.title.trim()) return 'Başlık zorunludur'
    const start = `${form.startDate}T${form.allDay ? '00:00' : form.startTime}:00`
    const end = `${form.endDate}T${form.allDay ? '00:00' : form.endTime}:00`
    if (end < start) return 'Bitiş başlangıçtan önce olamaz'
    const interval = Number(form.interval)
    if (form.recurrenceType === 'EVERY_N_DAYS' && !(interval >= 1 && interval <= 365)) return 'Gün aralığı 1-365 olmalıdır'
    if (form.recurrenceType === 'WEEKLY_DAYS' && form.weekdays.length === 0) return 'En az bir gün seçiniz'
    const monthDay = Number(form.monthDay)
    if ((form.recurrenceType === 'MONTHLY_DAY' || form.recurrenceType === 'YEARLY') && !(monthDay >= 1 && monthDay <= 31))
      return 'Gün 1-31 arasında olmalıdır'
    return {
      title: form.title.trim(),
      description: form.description.trim(),
      type: form.type,
      start,
      end,
      allDay: form.allDay,
      color: form.color,
      shared: form.shared,
      recurrenceType: form.recurrenceType,
      recurrenceInterval:
        form.recurrenceType === 'EVERY_N_DAYS' || form.recurrenceType === 'WEEKLY_DAYS' ? interval || 1 : null,
      recurrenceWeekdays: form.recurrenceType === 'WEEKLY_DAYS' ? [...form.weekdays].sort() : null,
      recurrenceMonthDay: form.recurrenceType === 'MONTHLY_DAY' || form.recurrenceType === 'YEARLY' ? monthDay : null,
      recurrenceMonth: form.recurrenceType === 'YEARLY' ? Number(form.month) : null,
      recurrenceUntil: form.recurrenceType !== 'NONE' && form.until ? form.until : null,
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const request = buildRequest()
    if (typeof request === 'string') {
      setError(request)
      return
    }
    onSubmit(request)
  }

  const preview = buildRequest()

  return (
    <Modal show={show} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered' backdrop='static'>
      <form onSubmit={handleSubmit} noValidate>
        <div className='modal-header'>
          <h5 className='modal-title'>{editing ? 'Düzenle (tüm tekrarlar)' : 'Yeni Görev / Etkinlik'}</h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>
        <div className='modal-body'>
          <div className='btn-group mb-5'>
            {(['EVENT', 'TASK'] as const).map((type) => (
              <button
                key={type}
                type='button'
                className={`btn btn-sm ${form.type === type ? 'btn-primary' : 'btn-light'}`}
                onClick={() => set('type', type)}
              >
                <i className={`bi ${type === 'TASK' ? 'bi-check2-square' : 'bi-calendar-event'}`}></i>
                {type === 'TASK' ? 'Görev' : 'Etkinlik'}
              </button>
            ))}
          </div>

          <div className='mb-5'>
            <label className='form-label fw-bold required'>Başlık</label>
            <input
              type='text'
              autoFocus
              maxLength={255}
              className='form-control'
              placeholder={form.type === 'TASK' ? 'Örn: Haftalık aday raporu' : 'Örn: Ekip toplantısı'}
              value={form.title}
              onChange={(event) => set('title', event.target.value)}
            />
          </div>

          <div className='form-check form-switch form-check-custom form-check-solid mb-4'>
            <input
              id='all-day'
              type='checkbox'
              className='form-check-input'
              checked={form.allDay}
              onChange={(event) => set('allDay', event.target.checked)}
            />
            <label className='form-check-label fw-semibold' htmlFor='all-day'>
              Tüm gün
            </label>
          </div>

          <div className='row g-4 mb-5'>
            <div className='col-md-6'>
              <label className='form-label fw-bold required'>Başlangıç</label>
              <div className='d-flex gap-2'>
                <input
                  type='date'
                  className='form-control'
                  value={form.startDate}
                  onChange={(event) => {
                    const value = event.target.value
                    setForm((current) => ({
                      ...current,
                      startDate: value,
                      endDate: current.endDate < value ? value : current.endDate,
                    }))
                  }}
                />
                {!form.allDay && (
                  <input
                    type='time'
                    className='form-control'
                    style={{maxWidth: 130}}
                    value={form.startTime}
                    onChange={(event) => set('startTime', event.target.value)}
                  />
                )}
              </div>
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold required'>Bitiş</label>
              <div className='d-flex gap-2'>
                <input
                  type='date'
                  className='form-control'
                  value={form.endDate}
                  onChange={(event) => set('endDate', event.target.value)}
                />
                {!form.allDay && (
                  <input
                    type='time'
                    className='form-control'
                    style={{maxWidth: 130}}
                    value={form.endTime}
                    onChange={(event) => set('endTime', event.target.value)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className='border rounded p-4 mb-5 bg-light'>
            <label className='form-label fw-bold'>
              <i className='bi bi-arrow-repeat me-1'></i>
              Tekrar
            </label>
            <select
              className='form-select mb-3'
              value={form.recurrenceType}
              onChange={(event) => set('recurrenceType', event.target.value as RecurrenceType)}
            >
              {RECURRENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {form.recurrenceType === 'EVERY_N_DAYS' && (
              <div className='d-flex align-items-center gap-2 mb-3'>
                <input
                  type='number'
                  min={1}
                  max={365}
                  className='form-control'
                  style={{maxWidth: 100}}
                  value={form.interval}
                  onChange={(event) => set('interval', event.target.value)}
                />
                <span>günde bir</span>
              </div>
            )}

            {form.recurrenceType === 'WEEKLY_DAYS' && (
              <div className='mb-3'>
                <div className='d-flex flex-wrap gap-2 mb-3'>
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day.value}
                      type='button'
                      title={day.label}
                      className={`btn btn-sm ${form.weekdays.includes(day.value) ? 'btn-primary' : 'btn-white border'}`}
                      onClick={() => toggleWeekday(day.value)}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='number'
                    min={1}
                    max={52}
                    className='form-control form-control-sm'
                    style={{maxWidth: 80}}
                    value={form.interval}
                    onChange={(event) => set('interval', event.target.value)}
                  />
                  <span className='fs-7'>haftada bir</span>
                </div>
              </div>
            )}

            {form.recurrenceType === 'MONTHLY_DAY' && (
              <div className='d-flex align-items-center gap-2 mb-3'>
                <span>Her ayın</span>
                <input
                  type='number'
                  min={1}
                  max={31}
                  className='form-control'
                  style={{maxWidth: 90}}
                  value={form.monthDay}
                  onChange={(event) => set('monthDay', event.target.value)}
                />
                <span>. günü</span>
                <span className='text-muted fs-8'>(ay kısaysa ayın son günü)</span>
              </div>
            )}

            {form.recurrenceType === 'YEARLY' && (
              <div className='d-flex align-items-center gap-2 mb-3'>
                <span>Her yıl</span>
                <input
                  type='number'
                  min={1}
                  max={31}
                  className='form-control'
                  style={{maxWidth: 90}}
                  value={form.monthDay}
                  onChange={(event) => set('monthDay', event.target.value)}
                />
                <select
                  className='form-select'
                  style={{maxWidth: 160}}
                  value={form.month}
                  onChange={(event) => set('month', event.target.value)}
                >
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {form.recurrenceType !== 'NONE' && (
              <>
                <div className='d-flex align-items-center gap-2'>
                  <span className='fs-7'>Bitiş tarihi (isteğe bağlı)</span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    style={{maxWidth: 170}}
                    min={form.startDate}
                    value={form.until}
                    onChange={(event) => set('until', event.target.value)}
                  />
                </div>
                {typeof preview !== 'string' && (
                  <div className='text-primary fs-7 mt-3'>
                    <i className='bi bi-info-circle me-1 text-primary'></i>
                    {recurrenceSummary(preview)}
                  </div>
                )}
              </>
            )}
          </div>

          <div className='mb-5'>
            <label className='form-label fw-bold'>Açıklama</label>
            <textarea
              rows={3}
              className='form-control'
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
            />
          </div>

          <div className='d-flex flex-wrap align-items-center justify-content-between gap-4'>
            <div className='d-flex align-items-center gap-2'>
              <span className='fw-semibold fs-7 me-1'>Renk</span>
              {EVENT_COLORS.map((color) => (
                <button
                  key={color}
                  type='button'
                  className='btn p-0 rounded-circle'
                  style={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    outline: form.color === color ? '3px solid #181c32' : 'none',
                    outlineOffset: 2,
                  }}
                  onClick={() => set('color', color)}
                />
              ))}
            </div>
            <div className='form-check form-switch form-check-custom form-check-solid'>
              <input
                id='shared'
                type='checkbox'
                className='form-check-input'
                checked={form.shared}
                onChange={(event) => set('shared', event.target.checked)}
              />
              <label className='form-check-label fw-semibold' htmlFor='shared'>
                Firmayla paylaş
              </label>
            </div>
          </div>

          {error && <div className='alert alert-danger mt-5 mb-0 py-3'>{error}</div>}
        </div>
        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={saving}>
            {saving ? <span className='spinner-border spinner-border-sm' /> : editing ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
